import React, {Dispatch, useEffect, useState} from 'react';
import {Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {Flow, FlowFetchResponse} from '../_services/Flow';
import {LayoutEntryGutterMetricsState} from '../_reducers/layout';
import {getLayoutEntryGutterMetrics} from '../_selectors/layout';
import {setLayoutEntryGutterMetrics} from '../_actions/layout';
import {connect} from 'react-redux';
import {Tooltip as HoverTooltip} from './Tooltip';

type EntryMetricsProps = {
    entry: any;
    layout: LayoutEntryGutterMetricsState;
    dispatchLayoutUpdate: (layout: LayoutEntryGutterMetricsState) => void;
};

type EntryMetricsResult = {
    summary: EntryMetrics[],
    detail: EntryMetrics[]
};

type EntryMetrics = {
    date_started: string;
    date_completed: string;
    step_id: string;
    step_name: string;
    step_type: string;
    duration_seconds: number;
    duration_minutes: number;
    duration_hours: number;
    duration_days: number;
};

const initialEntryMetricsResult: EntryMetricsResult = {
    summary: [],
    detail: []
}

const EntryMetrics: React.FunctionComponent<EntryMetricsProps> = ({
                                                                      entry,
                                                                      layout,
                                                                      dispatchLayoutUpdate
                                                                  }): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [metrics, setMetrics] = useState<EntryMetricsResult>({...initialEntryMetricsResult});

    // set the metrics key based on selected view
    const metricsKey = layout.totalCycleTime.view;

    useEffect(() => {
        if (!entry?.form?.id) {
            return;
        }

        setIsLoading(true);
        Flow.getInstance().getEntryStepCycleTime(entry.form.id, entry.id)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setMetrics(response.json);
                }
            })
            .catch((error: any) => {
                console.log('EntryMetrics error', error);
            })
            .finally(() => {
                setIsLoading(false);
            })
    }, [entry]);

    const swapMetricViewState = () => {
        dispatchLayoutUpdate({
            ...layout,
            totalCycleTime: {
                ...layout.totalCycleTime,
                view: layout.totalCycleTime.view === 'summary' ? 'detail' : 'summary'
            }
        })
    }

    const swapDisplayMode = () => {
        dispatchLayoutUpdate({
            ...layout,
            totalCycleTime: {
                ...layout.totalCycleTime,
                display: layout.totalCycleTime.display === 'graph' ? 'table' : 'graph'
            }
        })
    }

    if (isLoading) {
        return <></>;
    }

    // only render if we have data to show
    if (!(metrics?.summary?.length > 0 && metrics?.detail?.length > 0)) {
        return <></>;
    }

    const containerHeightCss = () => {
        const multiplier = (layout.totalCycleTime.display === 'graph') ? 25 : 35;
        const heightConst = (layout.totalCycleTime.display === 'graph') ? 30 : 30;
        let containerHeight = heightConst + (multiplier * metrics[metricsKey]?.length);

        if (containerHeight > 300) {
            containerHeight = 300;
        }
        return containerHeight + 'px';
    }

    const containerStyles = {
        height: `${containerHeightCss()}`,
        overflow: (layout.totalCycleTime.display === 'table') ? 'auto' : 'initial'
    };

    return (
        <div style={{border: '1px solid #e5e5e5'}}>
            <h3
                className="hndle d-flex justify-content-between align-items-center"
                style={{
                    cursor: 'default',
                    borderBottom: '1px solid #e5e5e5',
                    padding: '10px',
                    fontSize: '21px'
                }}
            >
                <span>
                    Total Cycle Time
                    <HoverTooltip text={'Time is measured between 6am - 6pm CST, Mon - Fri'}>
                    <sup><small><i className="fas fa-question-circle"/></small></sup>
                    </HoverTooltip>
                </span>
                <small>
                    <i
                        className={[
                            'far',
                            layout.display === 'default' && 'fa-minus-square',
                            layout.display !== 'default' && 'fa-plus-square'
                        ].filter(Boolean).join(' ')}
                        style={{cursor: 'pointer'}}
                        onClick={() => {
                            dispatchLayoutUpdate({
                                ...layout,
                                display: layout.display === 'default' ? 'minimized' : 'default'
                            })
                        }}
                    />
                </small>
                {/*
                    <i
                        className="fas fa-expand ml-2"
                        style={{
                            position: 'absolute',
                            right: 0,
                            marginRight: '.25rem'
                        }}
                    />
                    */}
            </h3>
            {layout.display === 'default' &&
                <>
                    <div style={containerStyles}>
                        {layout.totalCycleTime.display === 'graph' && <EntryMetricsGraph metrics={metrics} metricsViewState={layout.totalCycleTime.view}/>}
                        {layout.totalCycleTime.display === 'table' && <EntryMetricsTable metrics={metrics} metricsViewState={layout.totalCycleTime.view}/>}
                    </div>
                    <div className={'d-flex justify-content-between'} style={{borderTop: '1px solid #e5e5e5'}}>
                        <button type={'button'}
                                className={'btn btn-sm btn-link'}
                                onClick={swapDisplayMode}
                        >{layout.totalCycleTime.display === 'graph' ? 'Table' : 'Graph'}</button>

                        {metrics?.summary?.length !== metrics?.detail?.length &&
                            <button type={'button'}
                                    className={'btn btn-sm btn-link'}
                                    onClick={swapMetricViewState}
                            >{layout.totalCycleTime.view === 'summary' ? 'Full' : 'Summary'}</button>
                        }
                    </div>
                </>
            }
        </div>
    );
}

type EntryMetricsTableProps = {
    metrics: EntryMetricsResult,
    metricsViewState: 'summary' | 'detail'
}
const EntryMetricsTable: React.FunctionComponent<EntryMetricsTableProps> = ({
                                                                                metrics,
                                                                                metricsViewState
                                                                            }) => {
    const formatStepName = (stepName: string) => stepName;
    const formatDuration = (durationInSeconds: number) => {
        let seconds = durationInSeconds;
        let d = Math.floor(seconds / (3600 * 24));
        let h = Math.floor(seconds % (3600 * 24) / 3600);
        let m = Math.floor(seconds % 3600 / 60);
        let s = Math.floor(seconds % 60);

        let dDisplay = d > 0 ? d + (d === 1 ? ' d, ' : ' d, ') : '';
        let hDisplay = h > 0 ? h + (h === 1 ? ' h, ' : ' h, ') : '';
        let mDisplay = m > 0 ? m + (m === 1 ? ' m, ' : ' m, ') : '';
        let sDisplay = s > 0 ? s + (s === 1 ? ' s' : ' s') : '';
        return dDisplay + hDisplay + mDisplay + sDisplay;
    }

    return (
        <table className={'table table-sm'}>
            <thead>
            <tr>
                <th style={{border: 'none'}}>Step</th>
                <th style={{border: 'none'}}>Time</th>
            </tr>
            </thead>
            <tbody>
            {metrics[metricsViewState].map((m: EntryMetrics) => (
                <tr>
                    <td>{formatStepName(m.step_name)}</td>
                    <td>{formatDuration(m.duration_seconds)}</td>
                </tr>
            ))}
            </tbody>
        </table>
    );
}

type EntryMetricsGraphProps = {
    metrics: EntryMetricsResult,
    metricsViewState: 'summary' | 'detail'
}
const EntryMetricsGraph: React.FunctionComponent<EntryMetricsGraphProps> = ({
                                                                                metrics,
                                                                                metricsViewState
                                                                            }) => {
    const margin = {top: 0, right: 5, left: 10, bottom: 0};
    const tooltipFormatter = (value: any) => {
        let seconds = Number(value);
        let d = Math.floor(seconds / (3600 * 24));
        let h = Math.floor(seconds % (3600 * 24) / 3600);
        let m = Math.floor(seconds % 3600 / 60);
        let s = Math.floor(seconds % 60);

        let dDisplay = d > 0 ? d + (d === 1 ? ' day, ' : ' days, ') : '';
        let hDisplay = h > 0 ? h + (h === 1 ? ' hour, ' : ' hours, ') : '';
        let mDisplay = m > 0 ? m + (m === 1 ? ' minute, ' : ' minutes, ') : '';
        let sDisplay = s > 0 ? s + (s === 1 ? ' second' : ' seconds') : '';
        return [dDisplay + hDisplay + mDisplay + sDisplay];
    };

    const getYAxisDataKey = () => {
        return (metricsViewState === 'summary') ? 'step_name' : 'id';
    }

    return (
        <div className={'h-100'} style={{padding: '.25rem 0.25rem 0 0'}}>
            <ResponsiveContainer width="100%" height={'100%'}>
                <BarChart
                    layout={'vertical'}
                    data={metrics[metricsViewState]}
                    margin={margin}
                >
                    <YAxis
                        dataKey={getYAxisDataKey()}
                        type="category"
                        tick={{fontSize: 10, fill: '#aaa'}}
                        tickFormatter={(_value, idx) => {
                            const tickLabelLength = 11;
                            const tickLabel = metrics[metricsViewState][idx].step_name;
                            const tickLabelParts = tickLabel.split(' ');

                            if (tickLabelParts.length > 0) {
                                if (tickLabelParts[0].length > tickLabelLength) {
                                    return tickLabelParts[0].substr(0, tickLabelLength) + '…';
                                }
                                if (tickLabelParts.length > 1) {
                                    if (tickLabelParts[1].length > tickLabelLength) {
                                        return tickLabelParts[0] + ' ' + tickLabelParts[1].substr(0, tickLabelLength) + '…';
                                    } else {
                                        return tickLabelParts[0] + ' ' + tickLabelParts[1];
                                    }
                                }
                            }
                            return tickLabel;
                        }}
                    />
                    <XAxis
                        type="number"
                        tick={{fontSize: 10, fill: '#aaa'}}
                        tickFormatter={() => ''}
                        height={10}
                    />
                    <Tooltip
                        allowEscapeViewBox={{x: true, y: true}}
                        formatter={tooltipFormatter}
                        labelFormatter={(value, props) => {
                            console.log(props);
                            if (props?.length > 0) {
                                // @ts-ignore
                                return props[0].payload.step_name.replace(/Approval$/, '');
                            }
                            return value;
                        }}
                    />
                    {/*
                            <CartesianGrid vertical={true} stroke="#f5f5f5"/>
                            <XAxis dataKey="step_name" />
                        */}
                    <Bar dataKey={'duration_seconds'}
                         stroke="#4e79a7"
                         maxBarSize={20}
                         fill="rgba(78,121,167, 0.50)"
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

const mapStateToProps = (state: any) => ({
    layout: getLayoutEntryGutterMetrics(state)
});
const mapDispatchToProps = (dispatch: Dispatch<any>) => {
    return {
        dispatchLayoutUpdate: (layout: LayoutEntryGutterMetricsState) => {
            dispatch(setLayoutEntryGutterMetrics(layout));
        }
    };
};

const ConnectedEntryMetrics = connect(mapStateToProps, mapDispatchToProps)(EntryMetrics);
export {ConnectedEntryMetrics as EntryMetrics};